// Escalas de recuperación anestésica, compartidas por los bloques C
// (ingreso a UCPA), D (reevaluación, junto al gráfico de signos vitales de
// la Tanda 12) y H (egreso de UCPA) de 019 — Fase 1B, Tanda 15, ver
// PLAN.md. Las mismas 6 "escalas" (Aldrete Modificado, Bromage, Ramsay,
// EVA, Steward, Otro) se capturan 3 veces, una por momento — cada
// Observation lleva un `component` de `momento` para no pisarse entre
// capturas del mismo encuentro.
//
// Aldrete YA existía (antiguo stub de 019, un solo Observation con 5
// categorías como `component` + el puntaje total) — se extiende acá con el
// mismo `component` de `momento`, sin tocar su estructura interna. Las
// otras 5 son nuevas: cada una es su propio Observation, no components de
// un Observation compartido (a diferencia de Aldrete, no tienen
// sub-categorías que agrupar).
//
// Bromage usa valores fraccionarios (0/0.33/0.66/1) — FHIR R4 no tiene
// `valueDecimal` (ni a nivel de `Observation.value[x]` ni de
// `component.value[x]`), así que usa `valueQuantity` en vez de
// `valueInteger` (nota ya dejada en el plan de la Tanda 13).
//
// Nombre "Datos" (no `escalasRecuperacionAnestesica.ts`) por el mismo
// motivo que `tecnicaAnestesicaDatos.ts`: en Windows el filesystem no
// distingue mayúsculas, colisionaba con `EscalasRecuperacionAnestesica.tsx`.
import type { Encounter, Observation, ObservationComponent, Patient, Reference } from '@medplum/fhirtypes';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

export type Momento = 'ingreso' | 'reevaluacion' | 'egreso';

export const SISTEMA_ALDRETE = 'http://logiacare.ec/fhir/escala-aldrete';
export const SISTEMA_ESCALAS_RECUPERACION = 'http://logiacare.ec/fhir/escala-recuperacion-anestesica';

export const CATEGORIAS_ALDRETE = [
  { clave: 'actividad', codigo: 'actividad', etiqueta: 'Actividad muscular' },
  { clave: 'respiracion', codigo: 'respiracion', etiqueta: 'Respiración' },
  { clave: 'circulacion', codigo: 'circulacion', etiqueta: 'Circulación (presión arterial)' },
  { clave: 'conciencia', codigo: 'conciencia', etiqueta: 'Conciencia' },
  { clave: 'saturacion', codigo: 'saturacion', etiqueta: 'Saturación de oxígeno' },
] as const;

export const BROMAGE_OPCIONES = ['0', '0.33', '0.66', '1'] as const;
export const RAMSAY_OPCIONES = ['0', '1', '2', '3', '4', '5', '6'] as const;
export const EVA_OPCIONES = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'] as const;
export const STEWARD_OPCIONES = ['0', '1', '2', '3', '4', '5', '6'] as const;

export interface EstadoEscalasRecuperacion {
  readonly aldrete: Readonly<Record<string, number | ''>>;
  readonly bromage: string;
  readonly ramsay: string;
  readonly eva: string;
  readonly steward: string;
  readonly otro: string;
}

export function estadoInicialEscalas(): EstadoEscalasRecuperacion {
  return { aldrete: {}, bromage: '', ramsay: '', eva: '', steward: '', otro: '' };
}

export function puntajeTotalAldrete(aldrete: Readonly<Record<string, number | ''>>): number {
  return CATEGORIAS_ALDRETE.reduce((suma, c) => suma + (typeof aldrete[c.clave] === 'number' ? (aldrete[c.clave] as number) : 0), 0);
}

/** `numero` distingue varias capturas del mismo momento (las 6 reevaluaciones de 019); sin él (ingreso/egreso) el componente es el de siempre. */
function componenteMomento(momento: Momento, numero?: number): ObservationComponent[] {
  const componentes: ObservationComponent[] = [{ code: { coding: [{ system: SISTEMA_ESCALAS_RECUPERACION, code: 'momento' }] }, valueString: momento }];
  if (numero !== undefined) componentes.push({ code: { coding: [{ system: SISTEMA_ESCALAS_RECUPERACION, code: 'numero' }] }, valueInteger: numero });
  return componentes;
}

/** Un `Observation` por escala con datos (Aldrete + hasta 5 más), cada uno tageado con `momento`. Vacío si no se cargó ninguna escala. */
export function observacionesDeEscalas(subject: Reference<Patient>, encounter: Reference<Encounter> | undefined, momento: Momento, estado: EstadoEscalasRecuperacion, efectivo: string, numero?: number): Observation[] {
  const observaciones: Observation[] = [];
  const comunes = { subject, encounter, status: 'final' as const, effectiveDateTime: efectivo };

  const componentesAldrete: ObservationComponent[] = CATEGORIAS_ALDRETE.filter((c) => estado.aldrete[c.clave] !== '' && estado.aldrete[c.clave] !== undefined).map((c) => ({
    code: { coding: [{ system: SISTEMA_ALDRETE, code: c.codigo, display: c.etiqueta }] },
    valueInteger: estado.aldrete[c.clave] as number,
  }));
  if (componentesAldrete.length > 0) {
    observaciones.push({
      resourceType: 'Observation',
      ...comunes,
      code: { coding: [{ system: SISTEMA_ALDRETE, code: 'puntaje-total', display: 'Escala de Aldrete' }] },
      valueInteger: puntajeTotalAldrete(estado.aldrete),
      component: [...componentesAldrete, ...componenteMomento(momento, numero)],
    });
  }

  if (estado.bromage !== '') {
    observaciones.push({
      resourceType: 'Observation',
      ...comunes,
      code: { coding: [{ system: SISTEMA_ESCALAS_RECUPERACION, code: 'bromage', display: 'Escala de Bromage' }] },
      valueQuantity: { value: Number(estado.bromage), system: 'http://unitsofmeasure.org', code: '1' },
      component: componenteMomento(momento, numero),
    });
  }
  if (estado.ramsay !== '') {
    observaciones.push({
      resourceType: 'Observation',
      ...comunes,
      code: { coding: [{ system: SISTEMA_ESCALAS_RECUPERACION, code: 'ramsay', display: 'Escala de Ramsay' }] },
      valueInteger: Number(estado.ramsay),
      component: componenteMomento(momento, numero),
    });
  }
  if (estado.eva !== '') {
    observaciones.push({
      resourceType: 'Observation',
      ...comunes,
      code: { coding: [{ system: SISTEMA_ESCALAS_RECUPERACION, code: 'eva', display: 'Escala visual analógica (EVA)' }] },
      valueInteger: Number(estado.eva),
      component: componenteMomento(momento, numero),
    });
  }
  if (estado.steward !== '') {
    observaciones.push({
      resourceType: 'Observation',
      ...comunes,
      code: { coding: [{ system: SISTEMA_ESCALAS_RECUPERACION, code: 'steward', display: 'Escala de Steward' }] },
      valueInteger: Number(estado.steward),
      component: componenteMomento(momento, numero),
    });
  }
  if (estado.otro.trim() !== '') {
    observaciones.push({
      resourceType: 'Observation',
      ...comunes,
      code: { coding: [{ system: SISTEMA_ESCALAS_RECUPERACION, code: 'otro', display: 'Otra escala' }] },
      valueString: estado.otro.trim(),
      component: componenteMomento(momento, numero),
    });
  }

  return observaciones;
}

/** `Par[]` de respaldo para el PDF — mismos valores que `observacionesDeEscalas`, sin depender de que el guardado ya haya ocurrido. */
export function paresDeEscalas(t: Traducir, prefijoI18n: string, estado: EstadoEscalasRecuperacion): Par[] {
  const aldreteCargado = Object.values(estado.aldrete).some((v) => v !== '' && v !== undefined);
  const pares: Par[] = [
    { pregunta: t(`${prefijoI18n}.aldrete`), respuesta: aldreteCargado ? String(puntajeTotalAldrete(estado.aldrete)) : '' },
    { pregunta: t(`${prefijoI18n}.bromage`), respuesta: estado.bromage },
    { pregunta: t(`${prefijoI18n}.ramsay`), respuesta: estado.ramsay },
    { pregunta: t(`${prefijoI18n}.eva`), respuesta: estado.eva },
    { pregunta: t(`${prefijoI18n}.steward`), respuesta: estado.steward },
    { pregunta: t(`${prefijoI18n}.otro`), respuesta: estado.otro.trim() },
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
